import type { ActorEvent, CustomViewConfig, Pagination, RecordLikeProtocol, RichTextContentProtocol } from '@lighthouse/core'
import type { ContainerLayoutContextValue, FlowLayoutNode } from '@lighthouse/shared'
import { ContainerLayoutProvider, useContainerLayoutContext, ViewEmptyState } from '@lighthouse/shared'
import type { BreakPointSize } from '@lighthouse/tools'
import React, { forwardRef, memo, useMemo } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { CommentView } from './CommentView'

interface CustomCommentBlockProps {
    node: FlowLayoutNode
    breakPoint?: BreakPointSize
    readonly?: boolean
    pagination: Pagination
    replayCountMap: Record<string, number>
    onLoadMoreData: (pageNum: number) => Promise<RecordLikeProtocol[]>
    onLoadReplayData: (payload: { pageNum: number; pageSize: number; rootId: string }) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderRichText: (v: RichTextContentProtocol) => string
}

export const CustomCommentBlock = memo(
    forwardRef<HTMLDivElement, CustomCommentBlockProps>((props, ref) => {
        const { node, readonly, onRecordClick, replayCountMap, onLoadMoreData, onLoadReplayData, pagination, onRenderRichText } = props
        const { blockData, recordPoints, dataSource } = useViewBlockContext()
        const { id, title, config } = blockData
        const { breakPoint: baseBreakPointConfig, commentConfig, anchor } = config as CustomViewConfig
        const { anchorId } = anchor || {}
        const effectiveAnchorId = useEffectiveAnchorId(anchorId)
        useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
        const showEmpty = !dataSource || recordPoints.length === 0
        const { viewLayout, viewStyles, layout, emptyView } = baseBreakPointConfig

        const gap = viewLayout?.gap?.size || 0

        const { level } = useContainerLayoutContext()

        const contextValue: ContainerLayoutContextValue = useMemo(() => {
            return {
                parentId: id,
                level: level + 1,
                size: {
                    width: {
                        size: 'fill'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                layout: {
                    ...layout
                }
            }
        }, [id, level, layout])

        if (!commentConfig || !commentConfig.parentId || !commentConfig.rootId) {
            return null
        }

        if (showEmpty) {
            return <ViewEmptyState id={effectiveAnchorId} config={emptyView} onRenderRichText={onRenderRichText} />
        }

        return (
            <ContainerLayoutProvider value={contextValue}>
                <CommentView
                    id={effectiveAnchorId}
                    parentIdKey={commentConfig.parentId}
                    rootIdKey={commentConfig.rootId}
                    childIndent={viewLayout?.childIndent}
                    replayIconColor={viewStyles?.replyStyle?.iconColor}
                    replayTextColor={viewStyles?.replyStyle?.color}
                    node={node}
                    title={title}
                    gap={gap}
                    readonly={readonly}
                    onRecordClick={onRecordClick}
                    onLoadMoreData={onLoadMoreData}
                    pagination={pagination}
                    replayCountMap={replayCountMap}
                    onLoadReplayData={onLoadReplayData}
                />
            </ContainerLayoutProvider>
        )
    })
)
